import { useActorMember, useSubjectContext, useSubjectUpdate } from '@healthblocks-io/core'
import { isEmpty, isNil } from 'ramda'
import { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'

import { localUspSlidesAtom, UspSlidesPage } from '@/modules/app/pages/usp/UspSlides.page'
import { useLockoutTime } from '@/modules/shared/hooks/useLockoutTime'
import { useTenantHref } from '@/modules/shared/hooks/useTenantHref'

import type { IdentifierSystems, SubjectIdentifiers } from '@/modules/healthblocks/hooks/useLeanSubject'

const OnboardingRootPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [{ mutate }, updateSubject] = useSubjectUpdate()
  const { lockout } = useLockoutTime()
  const tenantHref = useTenantHref()

  const [seenUspSlides] = useRecoilState(localUspSlidesAtom)

  const actorMember = useActorMember()

  const subjectContext = useSubjectContext()
  const subjectMember = subjectContext.subject.data
  const subjectLoading = subjectContext?.subject?.isLoading

  // Template variables
  // ------------------------------------------------------------------------- /
  const identifiers: SubjectIdentifiers = useMemo(() => {
    return (
      subjectContext?.subject?.data?.identifiers?.reduce((accumulator, currentValue) => {
        accumulator[currentValue.system.toLowerCase() as IdentifierSystems] = currentValue.value

        return accumulator
      }, {} as SubjectIdentifiers) ?? ({} as SubjectIdentifiers)
    )
  }, [JSON.stringify(subjectContext?.subject?.data?.identifiers)])

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (subjectLoading) {
        return
      }

      if (!subjectMember || lockout) {
        const target = tenantHref('/login')
        navigate(target, { replace: true })
        return
      }

      if (!subjectMember?.meta.hasAcceptedPrivacyPolicy?.value) {
        // Privacy policy not accepted
        if (!location.pathname.endsWith('/onboarding/privacy-policy')) {
          const target = tenantHref('/onboarding/privacy-policy')
          navigate(target, { replace: true })
        }
        return
      }

      if (!subjectMember.meta.hasConfirmedContactInformation) {
        // Contact information not confirmed
        if (!location.pathname.endsWith('/onboarding/personal-contact-information')) {
          const target = tenantHref('/onboarding/personal-contact-information')
          navigate(target, { replace: true })
        }
        return
      }

      if (isNil(subjectMember?.identifiers) || isEmpty(subjectMember?.identifiers)) {
        if (location.pathname !== '/onboarding/activate-account') {
          const target = tenantHref('/onboarding/activate-account')
          navigate(target, { replace: true })
        }
        return
      }

      // No onboarding steps left
      if (!subjectMember.meta.onboarding?.completed) {
        // Refresh the user before updating the subject to make sure we have the latest metadata
        const latestSubject = await mutate()
        if (cancelled) return

        await updateSubject({
          meta: {
            ...latestSubject?.meta,
            onboarding: {
              ...latestSubject?.meta.onboarding,
              completed: true,
            },
            hasSeenAppUpdate: {
              version: APP_VERSION,
              date: new Date().toISOString(),
            },
          },
        })
        if (cancelled) return
      }

      const target = tenantHref('/')
      navigate(target, { replace: true })
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [subjectMember, actorMember, identifiers?.synops, location, navigate])

  return <>{seenUspSlides ? <Outlet /> : <UspSlidesPage />}</>
}

export { OnboardingRootPage }
